import type { CameraState, ProjectedPoint, Size } from '../types';
import {
  clampPitch,
  createProjector,
  getCameraBasis,
  type CameraBasis,
  type Projector,
} from '../geometry/projection';
import {
  toVisibleFaces,
  type BinnedColumn,
  type ColumnBox,
  type ColumnFace,
  type Point3,
  type Viewer,
} from '../geometry/binnedColumns';
import { toCssColor, type ColorScale, type Rgb } from './colorScale';
import { toSegmentBoxes, type ColumnPaints } from './columnPaint';
import {
  applyFog,
  applyShading,
  getShadingTerms,
  scaleShading,
  type LightingModel,
  type ShadingTerms,
} from './lighting';
import {
  drawTooltip,
  toComponentHeading,
  toComponentNameLines,
} from './renderTooltip';

const EDGE_ALPHA = 0.35;
const HIGHLIGHT_WIDTH = 2;

export type RenderColumnsArgs = {
  context: CanvasRenderingContext2D;
  /** Far to near. */
  order: ColumnBox[];
  viewer: Viewer;
  camera: CameraState;
  viewport: Size;
  colorScale: ColorScale;
  /** Colors each column in place of the ramp, solid or in stacked bands; null colors by height. */
  paints: ColumnPaints | null;
  lighting: LightingModel;
  shading: number;
  opacity: number;
  /** Outlines every visible face in this color, or none when null. */
  edgeColor: string | null;
  highlighted: BinnedColumn | null;
  highlightColor: string;
};

type FaceShading = (normal: Point3) => ShadingTerms;

const createFaceShading = (
  basis: CameraBasis,
  lighting: LightingModel,
  shading: number
): FaceShading => {
  const byNormal = new Map<string, ShadingTerms>();
  return normal => {
    const key = normal.join(',');
    const cached = byNormal.get(key);
    if (cached) return cached;
    const terms = scaleShading(
      getShadingTerms(normal, basis, lighting),
      shading
    );
    byNormal.set(key, terms);
    return terms;
  };
};

type ShadeFaceArgs = {
  base: Rgb;
  face: ColumnFace;
  projected: ProjectedPoint[];
  shadeFace: FaceShading;
  lighting: LightingModel;
  depthExtent: number;
};

const toFaceColor = ({
  base,
  face,
  projected,
  shadeFace,
  lighting,
  depthExtent,
}: ShadeFaceArgs): string => {
  const depth =
    projected.reduce((total, point) => total + point.depth, 0) /
    projected.length;
  const nearness = (depth + depthExtent) / (2 * depthExtent);
  return toCssColor(
    applyFog(applyShading(base, shadeFace(face.normal)), lighting.fog, nearness)
  );
};

/**
 * Whether a band's face is on the outside of its column: every side face, but only the top band's
 * top and the bottom band's bottom. The faces where two bands meet are inside the column, and
 * drawing one would paint over the band beside it wherever the eye sits level with the column.
 */
const isOuterFace = (
  { normal }: ColumnFace,
  band: number,
  bandCount: number
): boolean => {
  if (normal[2] > 0) return band === bandCount - 1;
  if (normal[2] < 0) return band === 0;
  return true;
};

const traceFace = (
  context: CanvasRenderingContext2D,
  projected: ProjectedPoint[]
): void => {
  context.beginPath();
  projected.forEach(({ screenX, screenY }, at) => {
    if (at === 0) {
      context.moveTo(screenX, screenY);
      return;
    }
    context.lineTo(screenX, screenY);
  });
  context.closePath();
};

const projectFace = (project: Projector, face: ColumnFace): ProjectedPoint[] =>
  face.corners.map(([x, y, z]) => project(x, y, z));

/**
 * Paints binned columns far to near, each visible face flat-lit by its own normal.
 *
 * Every face is stroked in its own fill color as well, which closes the antialiasing seam between
 * two faces of one box.
 */
export const renderColumns = ({
  context,
  order,
  viewer,
  camera,
  viewport,
  colorScale,
  paints,
  lighting,
  shading,
  opacity,
  edgeColor,
  highlighted,
  highlightColor,
}: RenderColumnsArgs): void => {
  if (order.length === 0) return;

  const project = createProjector(camera, viewport);
  const shadeFace = createFaceShading(
    getCameraBasis(camera.yaw, clampPitch(camera.pitch)),
    lighting,
    shading
  );
  const depthExtent = Math.hypot(1, 1, camera.heightScale);

  context.save();
  context.lineJoin = 'round';

  for (const box of order) {
    const segments = paints?.get(box.column.index);
    const stacked = segments
      ? toSegmentBoxes(box, segments)
      : [{ box, color: colorScale.forValue(box.column.value) }];
    const isHighlighted = box.column === highlighted;
    const outline = isHighlighted ? highlightColor : edgeColor;

    for (const [band, part] of stacked.entries()) {
      const faces = toVisibleFaces(part.box, viewer)
        .filter(face => isOuterFace(face, band, stacked.length))
        .map(face => ({
          face,
          projected: projectFace(project, face),
        }));

      for (const { face, projected } of faces) {
        const color = toFaceColor({
          base: part.color,
          face,
          projected,
          shadeFace,
          lighting,
          depthExtent,
        });
        traceFace(context, projected);
        context.fillStyle = color;
        context.strokeStyle = color;
        context.lineWidth = 1;
        context.globalAlpha = 1;
        context.fill();
        context.stroke();
      }

      if (!outline) continue;

      context.strokeStyle = outline;
      context.lineWidth = isHighlighted ? HIGHLIGHT_WIDTH : 1;
      context.globalAlpha = isHighlighted ? 1 : EDGE_ALPHA;
      for (const { projected } of faces) {
        traceFace(context, projected);
        context.stroke();
      }
    }
  }

  if (opacity < 1) {
    context.globalAlpha = 1;
    context.globalCompositeOperation = 'destination-in';
    context.fillStyle = `rgba(0, 0, 0, ${opacity})`;
    context.fillRect(0, 0, viewport.width, viewport.height);
  }

  context.restore();
};

export type ColumnTooltipArgs = {
  context: CanvasRenderingContext2D;
  box: ColumnBox;
  camera: CameraState;
  viewport: Size;
  xTitle: string;
  yTitle: string;
  zTitle: string;
  format: (value: number) => string;
  inkColor: string;
  surfaceColor: string;
  gridColor: string;
};

/** The lines a column's tooltip reads: its bin on both axes, its aggregate, and who is in it. */
export const toColumnTooltipLines = ({
  column,
  xTitle,
  yTitle,
  zTitle,
  format,
}: {
  column: BinnedColumn;
  xTitle: string;
  yTitle: string;
  zTitle: string;
  format: (value: number) => string;
}): string[] => [
  `${xTitle || 'x'}: ${format(column.xBounds[0])} – ${format(column.xBounds[1])}`,
  `${yTitle || 'y'}: ${format(column.yBounds[0])} – ${format(column.yBounds[1])}`,
  `${zTitle || 'z'}: ${format(column.value)}`,
  ...toComponentNameLines(column.points),
];

/** Names the hovered column, anchored at the center of its top. */
export const renderColumnTooltip = ({
  context,
  box,
  camera,
  viewport,
  xTitle,
  yTitle,
  zTitle,
  format,
  inkColor,
  surfaceColor,
  gridColor,
}: ColumnTooltipArgs): void => {
  const project = createProjector(camera, viewport);
  const anchor = project(
    (box.x[0] + box.x[1]) / 2,
    (box.y[0] + box.y[1]) / 2,
    box.z[1]
  );

  drawTooltip({
    context,
    anchor,
    heading: toComponentHeading(box.column.points),
    lines: toColumnTooltipLines({
      column: box.column,
      xTitle,
      yTitle,
      zTitle,
      format,
    }),
    viewport,
    inkColor,
    surfaceColor,
    gridColor,
  });
};
